import { z } from "zod";

/**
 * Where a Shopify variant's sale price stands once changed in the ERP (Settings › Integrations ›
 * "Push prices to Shopify"): waiting to be sent, carried by Shopify, or refused by it.
 */
export const SHOPIFY_PRICE_STATUSES = ["pending", "synced", "failed"] as const;
export type ShopifyPriceStatus = (typeof SHOPIFY_PRICE_STATUSES)[number];

/** External services the ERP connects to; each one is configured from Settings → Integrations. */
export const INTEGRATION_PROVIDERS = [
  "shopify",
  "email",
  "colissimo",
  "laposte",
  "chronopost",
  "chorus",
  "inqom",
  "iabako",
  "openai",
] as const;
export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number];

export type IntegrationFieldKind = "text" | "url" | "email" | "number" | "boolean" | "secret";

export interface IntegrationField {
  key: string;
  kind: IntegrationFieldKind;
  /** Needed for the integration to count as configured. */
  required?: boolean;
  placeholder?: string;
}

/** Field list per provider: drives both validation and the settings form. Labels live in i18n. */
export const INTEGRATION_FIELDS = {
  shopify: [
    { key: "shopDomain", kind: "text", required: true, placeholder: "phygiatech.myshopify.com" },
    // Dev Dashboard app: the ERP exchanges client ID + secret for a 24 h Admin token by itself.
    { key: "clientId", kind: "text" },
    { key: "webhookSecret", kind: "secret" },
    // Legacy custom app token, used instead of the client credentials when set.
    { key: "adminToken", kind: "secret", placeholder: "shpat_…" },
    { key: "locationId", kind: "text" },
    { key: "apiVersion", kind: "text", placeholder: "2026-07" },
    { key: "publicUrl", kind: "url", placeholder: "https://phygiatech.com" },
    // Off by default: a staging or local ERP must never overwrite the live shop's stock or fulfil its orders.
    { key: "writeBack", kind: "boolean" },
    // With writeBack on: a SAV exchange is noted on the Shopify order (note and tag, its line items stay untouched).
    { key: "syncExchanges", kind: "boolean" },
    // Off by default and apart from writeBack: a price changed in the ERP is sent to its Shopify variant
    // (price only). Kept separate so prices can go live without stock pushes, which need the stock entered.
    { key: "pushPrices", kind: "boolean" },
  ],
  email: [
    { key: "host", kind: "text", required: true, placeholder: "smtp.gmail.com" },
    { key: "port", kind: "number", placeholder: "587" },
    { key: "secure", kind: "boolean" },
    { key: "user", kind: "text" },
    { key: "password", kind: "secret" },
    { key: "fromName", kind: "text", placeholder: "Phygiatech" },
    { key: "fromEmail", kind: "email", required: true, placeholder: "contact@phygiatech.com" },
    // Where customers' replies go; the company e-mail when empty.
    { key: "replyTo", kind: "email" },
  ],
  // Either the Web Services key (Colissimo Box › profile, sent as the `apikey` header) or the former
  // contract number + password pair, which Colissimo still accepts for older setups.
  colissimo: [
    { key: "apiKey", kind: "secret" },
    { key: "contractNumber", kind: "text" },
    { key: "password", kind: "secret" },
  ],
  // La Poste "Suivi v2" (developer.laposte.fr): parcel tracking with a free Okapi key, no Colissimo contract needed.
  laposte: [{ key: "okapiKey", kind: "secret", required: true }],
  chronopost: [
    { key: "accountNumber", kind: "text", required: true },
    { key: "password", kind: "secret", required: true },
    // Product code of the labels: 01 Chrono 13 (default), 02 Chrono 10, 16 Chrono 18, 2R Chrono Relais.
    { key: "productCode", kind: "text", placeholder: "01" },
  ],
  chorus: [
    { key: "clientId", kind: "text", required: true },
    { key: "clientSecret", kind: "secret", required: true },
    { key: "techLogin", kind: "text", required: true },
    { key: "techPassword", kind: "secret", required: true },
    { key: "sandbox", kind: "boolean" },
  ],
  // Two ways to hand invoices to the accountant: the collection mailbox (a copy of the PDF by e-mail)
  // or the Inqom API (direct deposit as a "client bill" in the dossier). Either one makes it configured.
  inqom: [
    { key: "collectionEmail", kind: "email" },
    { key: "sandbox", kind: "boolean" },
    { key: "username", kind: "text", placeholder: "erp@phygiatech.com" },
    { key: "password", kind: "secret" },
    // Application keys handed out by Inqom (client_id defaults to "Web" until they give one).
    { key: "clientId", kind: "text", placeholder: "Web" },
    { key: "clientSecret", kind: "secret" },
    { key: "enterpriseId", kind: "number", placeholder: "12345" },
  ],
  // The previous tool, read (never written) by a headless browser until the switch-over: an
  // IABAKO user's login. IABAKO has no public API, so the ERP signs in as this user.
  iabako: [
    { key: "email", kind: "email", required: true, placeholder: "prenom@phygiatech.com" },
    { key: "password", kind: "secret", required: true },
  ],
  // The ERP assistant (modules/assistant): the OpenAI key it calls the model with, and the model.
  openai: [
    { key: "apiKey", kind: "secret", required: true, placeholder: "sk-…" },
    { key: "model", kind: "text", placeholder: "gpt-5-mini" },
  ],
} as const satisfies Record<IntegrationProvider, readonly IntegrationField[]>;

/**
 * Update payload. Plain fields: a value, or null to clear. Secrets: a new value to store,
 * null to delete, or absent to keep the stored one (the browser never sees it).
 */
export const integrationUpdateSchema = z.object({
  values: z.record(z.string(), z.union([z.string().trim().max(500), z.boolean(), z.null()])).default({}),
  secrets: z.record(z.string(), z.union([z.string().trim().min(1, "errors.field.required").max(4000), z.null()])).default({}),
});
export type IntegrationUpdateInput = z.input<typeof integrationUpdateSchema>;
export type IntegrationUpdateData = z.output<typeof integrationUpdateSchema>;

export const integrationTestSchema = z.object({ to: z.email("errors.field.email").optional() });
